import { forwardRef, useEffect, useRef, useState } from 'react'

import { InputError, Typography, Tooltip } from 'primsell-shared-layer/components'
import { InputLabel } from 'primsell-shared-layer/components/atoms/input-label'

import { StyledTextArea, StyledTextAreaWrapper, StyledMaxLength } from './text-area.styles'

import { ITextareaProps } from './text-area.types'

const TextArea = forwardRef<HTMLTextAreaElement, ITextareaProps>(
  (
    {
      placeholder,
      onChange,
      name,
      value,
      error,
      label,
      isRequired,
      disabled,
      className,
      rows,
      tooltipTitle,
      inputTooltip,
      maxLength,
      autoResize,
      helperText,
      secondaryLabel,
      ...props
    }: ITextareaProps,
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false)

    const handleClick = () => {
      setIsFocused(true)
    }

    const handleBlur = () => {
      setIsFocused(false)
    }

    const isExceed = !!value?.length && !!maxLength && value.length > maxLength

    const ownRef = useRef<HTMLTextAreaElement | null>(null)

    useEffect(() => {
      if (autoResize && !ref && ownRef?.current) {
        ownRef.current.style.height = '0px'
        const scrollHeight = ownRef.current.scrollHeight
        ownRef.current.style.height = scrollHeight + 'px'
      }
    }, [autoResize, ref, value])

    return (
      <StyledTextAreaWrapper className={className}>
        {label && (
          <InputLabel label={label} isRequired={isRequired} helperText={helperText} tooltipTitle={tooltipTitle} />
        )}
        {secondaryLabel}
        <Tooltip {...inputTooltip} disabled={!inputTooltip || inputTooltip.disabled}>
          <StyledTextArea
            name={name}
            placeholder={!isFocused ? placeholder : ''}
            value={value || ''}
            onChange={onChange}
            onClick={handleClick}
            onBlur={handleBlur}
            disabled={disabled}
            ref={ref ?? ownRef}
            rows={rows}
            error={!!error}
            autoResize={autoResize}
            {...props}
          />
        </Tooltip>
        {maxLength && (
          <StyledMaxLength isExceed={isExceed}>
            <Typography variant="h5" color="grey">
              {value?.length.toLocaleString() ?? 0} / {maxLength?.toLocaleString()}
            </Typography>
          </StyledMaxLength>
        )}
        {error && <InputError error={error} />}
      </StyledTextAreaWrapper>
    )
  }
)

TextArea.displayName = 'text-area'

export { TextArea }
